import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import SmartLoadingBuffer from './SmartLoadingBuffer';
import useAuth from '../../hooks/useAuth';

const AuthenticationGuard = ({
  children,
  requireAuth = true,
  redirectTo = '/login',
  fallbackRedirect = '/dashboard',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, initializing } = useAuth();

  useEffect(() => {
    if (initializing) return;

    if (!requireAuth && isAuthenticated) {
      navigate(fallbackRedirect, { replace: true });
    }
  }, [fallbackRedirect, initializing, isAuthenticated, navigate, requireAuth]);

  if (initializing) {
    return (
      <div className="min-h-screen bg-background">
        <SmartLoadingBuffer
          className="min-h-screen"
          title="Restoring your workspace"
          message="We’re verifying your session and syncing personalized insights."
          hint="Cogito is preparing your learning environment."
        />
      </div>
    );
  }

  if (requireAuth && !isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 bg-warning/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="Lock" size={24} color="var(--color-warning)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Authentication Required</h2>
            <p className="text-muted-foreground mb-6">
              Please sign in to access your learning dashboard and continue your progress.
            </p>
            <button
              onClick={() => navigate(redirectTo, { replace: true, state: { from: location } })}
              className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Sign In to Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!requireAuth && isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto">
            <Icon name="CheckCircle" size={24} color="var(--color-success)" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-foreground mb-2">Already Signed In</h2>
            <p className="text-muted-foreground mb-6">
              You're already authenticated. Redirecting to your dashboard...
            </p>
            <button
              onClick={() => navigate(fallbackRedirect, { replace: true })}
              className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default AuthenticationGuard;